import React, { useEffect, useMemo, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Axios } from "api/axios";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Autocomplete, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import {
  addTeam,
  fetchTeamById,
  updateTeam,
} from "slices/UserManagement/team/thunk";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchOrganizationDropdown } from "slices/UserManagement/organisation/thunk";
import LoadingSpinner from "components/LoadingSpinner";
import { fetchDepartmentsByOrganisationDropDown } from "slices/UserManagement/department/thunk";
import BootstrapTextField from "Common/BootstrapTextField";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";

const TeamstForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState<Boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [organisations, setOrganisations] = useState<
    { organization_id: string; organization_name: string }[]
  >([]);

  const [departments, setDepartments] = useState<
    { department_id: string; department_name: string }[]
  >([]);

  const formik = useFormik({
    initialValues: {
      organisationId: "",
      departmentId: "",
      team_name: "",
    },
    validationSchema: Yup.object({
      organisationId: Yup.string().required("Organisation is required"),
      departmentId: Yup.string().required("Department is required"),
      team_name: Yup.string().trim().required("Team Name is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        setIsSubmitting(true);

        const payload = {
          organization_id: values.organisationId,
          department_id: values.departmentId,
          team_name: values.team_name.trim(),
          ...(id && { team_id: id }),
        };

        if (id) {
          await dispatch(updateTeam({ id, data: payload })).unwrap();
        } else {
          await dispatch(addTeam(payload)).unwrap();
          clearTableState(TableKeys?.USER_MANAGEMENT?.TEAM);
        }

        toast.success(`Team ${id ? "updated" : "created"} successfully`);
        navigate("/team-list");
      } catch (error: any) {
        // 🔥 error here is rejectWithValue payload
        toast.error(error?.error || error?.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  // Initial Organization DropDown
  useEffect(() => {
    const fetchOrganisation = async () => {
      try {
        const res = await dispatch(fetchOrganizationDropdown()).unwrap();
        setOrganisations(res || []);
      } catch (error) {
        toast.error("Failed to load organisation");
      }
    };
    fetchOrganisation();
  }, []);

  useEffect(() => {
    if (!formik.values.organisationId) {
      setDepartments([]);
      formik.setFieldValue("departmentId", "");
      return;
    }
    const fetchDeptByOrg = async () => {
      try {
        const res = await dispatch(
          fetchDepartmentsByOrganisationDropDown(formik.values.organisationId),
        ).unwrap();
        setDepartments(res?.data || []);
      } catch (error: any) {
        toast.error(error?.message || "Failed to load Departments");
      }
    };
    fetchDeptByOrg();
  }, [formik.values.organisationId]);

  useEffect(() => {
    setLoading(true);
    const fetchTeam = async () => {
      try {
        if (id) {
          const res = await dispatch(fetchTeamById(id)).unwrap();
          formik.setValues({
            organisationId: res?.data?.data?.organization_id || "",
            departmentId: res?.data?.data?.department_id || "",
            team_name: res?.data?.data?.team_name || "",
          });
        }
      } catch (error: any) {
        toast.error(error?.data?.data?.message || "Failed to load Details");
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [id]);

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Team" : "Add Team"}
        titleLink="/team-list"
        breadcrumbs={[
          { label: "Teams", to: "/team-list" },
          { label: id ? "Edit Team" : "Add Team", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Row className="mb-4">
            {/* Organisation */}
            <Col md={4} xs={12} className="mb-3 mb-md-0">
              <Form.Label className="fw-semibold">
                Organisation <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                options={organisations}
                getOptionLabel={(option: any) => option.organization_name || ""}
                value={
                  organisations.find(
                    (org) =>
                      org.organization_id === formik.values.organisationId,
                  ) || null
                }
                onChange={(_, value) => {
                  formik.setFieldValue(
                    "organisationId",
                    value?.organization_id || "",
                  );
                }}
                onBlur={formik.handleBlur}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) =>
                      typeof opt === "string"
                        ? opt
                        : String(opt?.organization_name),
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Organisation"
                    id="organisationId"
                    name="organisationId"
                    error={
                      formik.touched.organisationId &&
                      Boolean(formik.errors.organisationId)
                    }
                    helperText={
                      formik.touched.organisationId &&
                      formik.errors.organisationId
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Col>

            {/* Department */}
            <Col md={4} xs={12} className="mb-3 mb-md-0">
              <Form.Label className="fw-semibold">
                Department <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                options={departments}
                getOptionLabel={(option: any) => option.department_name || ""}
                value={
                  departments?.find(
                    (dept) =>
                      dept?.department_id === formik?.values?.departmentId,
                  ) || null
                }
                onChange={(_, value) => {
                  formik.setFieldValue(
                    "departmentId",
                    value?.department_id || "",
                  );
                }}
                onBlur={formik.handleBlur}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) =>
                      typeof opt === "string"
                        ? opt
                        : String(opt?.department_name),
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Department"
                    id="departmentId"
                    name="departmentId"
                    error={
                      formik.touched.departmentId &&
                      Boolean(formik.errors.departmentId)
                    }
                    helperText={
                      formik.touched.departmentId && formik.errors.departmentId
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    fullWidth
                    size="small"
                  />
                )}
                disabled={departments.length === 0}
              />
            </Col>

            {/* Team Name */}
            <Col md={4} xs={12}>
              <Form.Label className="fw-semibold">
                Team Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                id="team_name"
                name="team_name"
                placeholder="Enter Team Name"
                value={formik.values.team_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.team_name && !!formik.errors.team_name
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.team_name}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton redirectTo="/team-list" disabled={isSubmitting} />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default TeamstForm;
